import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Button,
  ContentSwitcher,
  IconButton,
  InlineNotification,
  SkeletonText,
  Switch,
} from '@carbon/react';
import {
  CaretLeft,
  CaretRight,
  Close,
  Information,
  Renew,
} from '@carbon/react/icons';
import type { SlotValuationInputsResponse } from 'types/packages-api/slot-valuation-inputs';
import type { SlotOverrideLogEntry, SlotOverrideOperation } from '../types';
import { useSlotValuationInputs } from './hooks';
import { TearsheetTableView } from './tearsheet-table-view';
import { TearsheetModelMap } from './tearsheet-model-map';
import { TearsheetLink } from './tearsheet-link';
import { TearsheetExportButton } from './tearsheet-export-button';
import { EMPTY, tearsheetErrorSubtitle } from './utils';
import type { CalcRunState } from '../hooks';
import styles from './slot-tearsheet.module.scss';

type TearsheetView = 'table' | 'model_map';

// Focus-trappable elements inside the dialog. `:not([disabled])` keeps a disabled prev/next
// arrow out of the cycle; `[tabindex="-1"]` (the panel itself) is excluded so Tab never lands
// back on the container.
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface SlotTearsheetProps {
  /** Whether the modal is shown. Drives the fetch gate. */
  open: boolean;
  /** Close handler — the modal is controlled by the caller. */
  onClose: () => void;
  /** The package the slot belongs to. */
  packageId: number;
  /** The active eval-calc id. `null` in package mode — the caller should not open then. */
  calculationId: number | null;
  /** The slot's row identity (`TractNRI.slotId`), or `null` when nothing is open. */
  slotId: string | null;
  /** The ordered slot ids on the page — drives prev/next navigation. */
  slotIds?: string[];
  /** Navigate to another slot's tearsheet (prev/next), keeping the modal open. */
  onNavigate?: (slotId: string) => void;
  /**
   * slot-view's own `commitSlotOverride`, threaded straight through to the table view.
   *
   * PASSED, NOT REBUILT. This component is rendered by slot-view (slot-view.tsx:1231), the
   * very component that owns that handler, so the tearsheet writes through the same path as
   * a grid edit and a bulk edit. Optional: a caller with no override log omits it and the
   * rows render read-only.
   */
  onCommitOverride?: (operation: SlotOverrideOperation) => void;
  /**
   * The route offered from a pending override: close this tearsheet and
   * leave the user at the existing Calculate control.
   *
   * A ROUTE, NOT A RUN, and deliberately not this component's own close handler dressed up:
   * the caller decides what "go to Calculate" means on its surface (slot-view also opens the
   * Eval Settings panel). Omitted by a caller with no run control, and the route then is not
   * offered at all rather than rendering a button that goes nowhere.
   */
  onRerun?: () => void;
  /**
   * The working config's PENDING override operations, threaded to the read.
   *
   * WITHOUT IT THE PANEL CANNOT SEE ITS OWN EDITS. An edit made here lands in the working log
   * and the tearsheet re-reads the run's COMMITTED log only, so nothing changes and the edit
   * looks lost. The caller owns the log (slot-view holds the form), so it is passed rather
   * than re-derived — the same reasoning as `onCommitOverride` directly above.
   */
  slotOverrides?: SlotOverrideLogEntry[];
  /**
   * The SCHEDULE DIALOG's committed date edits — the second override channel, threaded to the
   * same read.
   *
   * WITHOUT IT THE BOUNDS ARE STALE. Calculate composes both channels; the tearsheet read
   * composed one, so a date moved in the dialog left this panel clamping to a neighbour the
   * run would not use, and Calculate then rejected the day the picker had offered. Passed
   * rather than re-derived, for the same reason `slotOverrides` is: slot-view holds the form.
   */
  scheduleSlotOverrides?: Array<Record<string, unknown>>;
}

/**
 * Composes the header subtitle from the tearsheet header — property name, unit,
 * basin/tier, the active case (or "No case selected"), and the resolved-input
 * count. Every field is null-tolerant (contract `SlotMetadata`).
 *
 * @param {SlotValuationInputsResponse} data - The tearsheet payload.
 * @return {string} The subtitle line.
 */
function caseText(data: SlotValuationInputsResponse): string {
  const { activeCase, mode } = data.header;
  if (activeCase) {
    return activeCase.name ?? `Case #${activeCase.id} (unavailable)`;
  }
  return mode === 'legacy' ? 'No case (legacy run)' : 'No case selected';
}

/**
 * The header subtitle — property name, unit, basin/tier, the active case and the
 * resolved-input count. Every field is null-tolerant (contract `SlotMetadata`).
 *
 * ── A COMPONENT RATHER THAN A STRING (UAT 8) ─────────────────────────────────────────────
 *
 * It composed one string, which cannot carry a link. Andrew asked for the unit name to reach
 * the unit page, so the one segment that links is rendered as an element and the rest is
 * unchanged text in the same order and with the same separators.
 *
 * @param {object} props - The tearsheet payload.
 * @return {JSX.Element} The subtitle line.
 */
function Subtitle({ data }: { data: SlotValuationInputsResponse }) {
  const { slot } = data.header;
  const basinTier = [slot.basin, slot.tier].filter(Boolean).join(' ');
  const resolved = data.slotInputs.filter((node) => node.applied).length;
  // Same order and separators as the string this replaced: wellName • unit • basin/tier.
  const parts: ReactNode[] = [];
  if (slot.wellName) {
    parts.push(slot.wellName);
  }
  if (slot.unit?.name) {
    // LINKED ONLY WHEN THE SERVER RESOLVED ONE. `/development_areas/{name}`, served
    // pre-resolved — never built here. Absent href renders the name as plain text.
    parts.push(
      slot.unit.href ? (
        <TearsheetLink key="unit" href={slot.unit.href}>
          {slot.unit.name}
        </TearsheetLink>
      ) : (
        slot.unit.name
      )
    );
  }
  if (basinTier) {
    parts.push(basinTier);
  }

  return (
    <p className={styles.subtitle}>
      {parts.length ? 'Property: ' : 'Property'}
      {parts.map((part, index) => (
        <span key={index}>
          {index > 0 && ' • '}
          {part}
        </span>
      ))}
      {` | Case: ${caseText(data)} • ${resolved} resolved inputs`}
    </p>
  );
}

/**
 * The copy for a body the query cannot fill. Offline is reported as offline (actionable);
 * otherwise the calc's own state is named.
 *
 * @param {CalcRunState} calcState - The viewed calc's lifecycle state.
 * @param {string} fetchStatus - React Query's fetchStatus ('paused' when offline).
 * @return {{ title: string, subtitle: string }} The notification copy.
 */
function blockedStateCopy(
  calcState: CalcRunState,
  fetchStatus: string
): { title: string; subtitle: string } {
  if (fetchStatus === 'paused') {
    return {
      title: 'You appear to be offline',
      subtitle:
        'The tearsheet could not be loaded. It will load once the connection returns.',
    };
  }
  if (calcState === 'processing') {
    return {
      title: 'Calculation in progress',
      subtitle:
        'This calculation is still running. Its values will be available once it finishes.',
    };
  }
  if (calcState === 'error') {
    return {
      title: 'Calculation did not complete',
      subtitle:
        'This calculation finished without results, so it has no values to show.',
    };
  }
  return {
    title: 'No values to show',
    subtitle: 'This calculation has no values for this slot yet.',
  };
}

/**
 * The body shown when the tearsheet query is DISABLED or PAUSED — the states React Query's own
 * flags collapse into "not loading, no data, no error". Without it the modal rendered a
 * full-screen panel with a blank white body (INS-11969 review): a Processing / Error calc
 * reaches here (the query gates on a completed calc), as does offline (React Query pauses
 * the fetch). A still-`resolving` calc is a genuine wait and is handled by the skeleton in
 * the caller, so it never reaches here.
 *
 * @param {object} props - The calc lifecycle state and the query's fetchStatus.
 * @return {JSX.Element} An explanatory notification, never a blank panel.
 */
function TearsheetBlockedState({
  calcState,
  fetchStatus,
}: {
  calcState: CalcRunState;
  fetchStatus: string;
}) {
  const { title, subtitle } = blockedStateCopy(calcState, fetchStatus);
  return (
    <div className={styles.errorBlock}>
      <InlineNotification
        kind="info"
        lowContrast
        hideCloseButton
        title={title}
        subtitle={subtitle}
      />
    </div>
  );
}

/**
 * The slot valuation tearsheet — a large centered modal opened from the DataView ICON in
 * the PROPERTIES table's Slot ID cell (slot-view.tsx SlotIdCell). Fetches one slot's
 * `slot_valuation_inputs` payload and renders it in the TABLE view (per-zone
 * Parameter/Value tables with scenario/area + formulas, then the Calculated result), with
 * a toggle to the MODEL MAP view (the `model_map` registry diagram). The header carries
 * prev/next slot navigation across the page's slots. The model-map view mounts only when its
 * tab is active, but the map REQUEST is not deferred with it: the table view needs the
 * registry's units/labels, so `useModelMap` fires on open regardless of tab (see useModelMap).
 *
 * Controlled by the caller: `open` gates the fetch, so a closed modal issues no
 * request and holds no diagram.
 *
 * @param {SlotTearsheetProps} props - Open state, close handler, slot identity + navigation.
 * @return {JSX.Element | null} The tearsheet modal, or null when closed.
 */
export function SlotTearsheet({
  open,
  onClose,
  packageId,
  calculationId,
  slotId,
  slotIds = [],
  onNavigate,
  onCommitOverride,
  onRerun,
  slotOverrides,
  scheduleSlotOverrides,
}: SlotTearsheetProps) {
  const [view, setView] = useState<TearsheetView>('table');
  const modalRef = useRef<HTMLDivElement>(null);

  // Reset to the table view whenever the modal opens (not on every slot change —
  // prev/next keeps the current view).
  useEffect(() => {
    if (open) {
      setView('table');
    }
  }, [open]);

  // Close on Escape while open.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  // ── FOCUS MANAGEMENT FOR THE HAND-ROLLED DIALOG (a11y, INS-11969 review) ─────────────────
  //
  // This panel is a plain `position: fixed` div, not a Carbon ComposedModal, so nothing moves
  // focus for it. With `aria-modal="true"` (which tells assistive tech to ignore everything
  // OUTSIDE the dialog) and focus never moved in, a screen-reader user was left on a node the
  // AT had just been told to ignore, and Tab walked them straight back out. So: move focus into
  // the panel on open, trap Tab within it while open, and restore focus to the trigger on close.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const panel = modalRef.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Focus the dialog container itself — role="dialog" + aria-label announces the tearsheet,
    // and it stays valid while the body swaps between skeleton, data and the blocked state.
    panel?.focus();

    const focusables = (): HTMLElement[] => {
      if (!panel) {
        return [];
      }
      return Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      );
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !panel) {
        return;
      }
      const items = focusables();
      if (items.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (event.shiftKey && (active === first || active === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      } else if (!active || !panel.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    };

    panel?.addEventListener('keydown', onKeyDown);
    return () => {
      panel?.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [open]);

  // `isLoading`, NOT `isPending`: React Query reports `status: 'pending'` for a DISABLED
  // query forever, and this query is gated on a COMPLETED calc (hooks.ts) while the
  // Slot ID cell opens the tearsheet whenever calculationId is non-null — including a
  // Processing or Error calc. `isPending` there would spin the skeleton with no request in
  // flight and no way out; `isLoading` (isPending && isFetching) is false when the
  // query never runs, matching the sibling eval3-new hooks.
  const {
    data: servedData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
    fetchStatus,
    calcState,
  } = useSlotValuationInputs({
    packageId,
    calculationId,
    slotId,
    enabled: open,
    slotOverrides,
    scheduleSlotOverrides,
  });

  const { prevSlot, nextSlot } = useMemo(() => {
    const index = slotId ? slotIds.indexOf(slotId) : -1;
    return {
      prevSlot: index > 0 ? slotIds[index - 1] : null,
      nextSlot:
        index >= 0 && index < slotIds.length - 1 ? slotIds[index + 1] : null,
    };
  }, [slotId, slotIds]);

  // The table body's four MUTUALLY-EXCLUSIVE states. A still-`resolving` calc folds into the
  // skeleton (a genuine wait); everything the query cannot fetch — processing / error / a paused
  // offline fetch — routes to the explanatory block instead of the blank panel it used to render
  // (INS-11969 review). `data`/`isError` win when they are present.
  // ── ⚠️ A PAYLOAD MUST NOT OUTLIVE THE SLOT IT DESCRIBES ──────────────────────────────
  //
  // `slotId` is in the query key and the query carries `placeholderData: keepPreviousData`
  // — added for the edit case, so an EDIT does not blank this panel to a skeleton. Across a
  // SLOT CHANGE the same option hands back the PREVIOUS SLOT's payload: the heading above
  // renders the `slotId` prop while the subtitle and the whole table render `data`, so the
  // panel showed slot A's numbers under slot B's name until B's request landed.
  //
  // ⚠️ AND IT WAS NOT ONLY A DISPLAY FAULT. `overridable` rides on the MODEL MAP node, not
  // on the slot row (editability.ts), so every edit and revert affordance stayed live in
  // that window — `isFetching` gates only the error state's "Try again" button. The write
  // handler takes the `slotId` PROP (use-tearsheet-edit.ts:111) while the value, the field
  // and the date bounds come from the payload on screen, so a click wrote to slot B
  // carrying slot A's data. Measured: a revert built from A's rows fired against B.
  //
  // THE PAYLOAD NAMES ITS OWN SLOT — `header.slotId`, served from the request's own
  // `slot_id` (slot_valuation_inputs.rb:977) — so the panel can simply decline one that is
  // not the slot it was asked for. Chosen over a `key` on the caller because it is
  // self-contained (no caller can defeat it by forgetting the key) and because it closes
  // the WRITE path as well as the display: the table never mounts, so there is nothing to
  // click.
  //
  // ⚠️ BHARGAV'S H IS UNTOUCHED. A same-slot edit moves only the PENDING half of the key,
  // so `header.slotId` still matches, the previous payload is still shown, and the panel
  // still does not blank. Pinned both ways in slot-carry-over.test.tsx.
  const data =
    servedData && servedData.header.slotId === slotId ? servedData : undefined;

  // AND THE SKELETON COVERS THE GAP, so declining the stale payload never leaves the user
  // staring at an empty panel: a request IS in flight for the slot named in the heading,
  // which is exactly what the skeleton says.
  const showSkeleton =
    isLoading ||
    calcState === 'resolving' ||
    (servedData !== undefined && data === undefined);
  const showBlocked = !showSkeleton && !isError && !data;

  if (!open) {
    return null;
  }

  return (
    <>
      <div className={styles.overlay} onClick={onClose} aria-hidden />
      <div
        className={styles.modal}
        role="dialog"
        aria-label="Slot valuation tearsheet"
        aria-modal="true"
        ref={modalRef}
        tabIndex={-1}
      >
        <header className={styles.header}>
          <div className={styles.titleBlock}>
            <div className={styles.titleRow}>
              <IconButton
                label="Previous slot"
                kind="ghost"
                size="sm"
                disabled={!prevSlot || !onNavigate}
                onClick={() => prevSlot && onNavigate?.(prevSlot)}
              >
                <CaretLeft />
              </IconButton>
              {/* ── UAT 8: THE UWI LINKS TO ITS WELL ────────────────────────────────
                  `wellHref` is null on a slot with no well — an unassigned LTD's identity
                  is the composite devArea_formation_slot, not a UWI — and that renders as
                  plain text rather than a route invented from the slot id. */}
              <h2 className={styles.title}>
                {data?.header.slot.wellHref ? (
                  <TearsheetLink href={data.header.slot.wellHref}>
                    {slotId}
                  </TearsheetLink>
                ) : (
                  slotId
                )}
              </h2>
              <IconButton
                label="Next slot"
                kind="ghost"
                size="sm"
                disabled={!nextSlot || !onNavigate}
                onClick={() => nextSlot && onNavigate?.(nextSlot)}
              >
                <CaretRight />
              </IconButton>
            </div>
            {data ? (
              <Subtitle data={data} />
            ) : (
              <p className={styles.subtitle}>{EMPTY}</p>
            )}
          </div>
          <IconButton
            label="Close"
            kind="ghost"
            size="sm"
            onClick={onClose}
            className={styles.closeButton}
          >
            <Close />
          </IconButton>
        </header>

        <div className={styles.toolbar}>
          <div className={styles.toolbarLeft}>
            <h3 className={styles.toolbarTitle}>Property Valuation Inputs</h3>
            <IconButton
              label="Every value the calculator resolved for this slot, grouped by domain with its scenario, area, and formula."
              kind="ghost"
              size="sm"
              align="bottom"
            >
              <Information />
            </IconButton>
            {/* ── THE TWO-SWATCH LEGEND IS GONE, AND IT WAS NOT MERELY UNSTYLED ────────
                It advertised "Used for this slot" (blue) and "User override" (yellow), and
                `legendUsed` / `legendOverride` coloured nothing but their own swatches.
                Establishing which of the two possible faults it was, measured 5 Sep 2026:

                USED FOR THIS SLOT — the distinction IS computed, by
                isUnappliedCategoryRate, and IS drawn, as `opacity: 0.45` on the rates
                belonging to another reserve category. What does not exist is anything
                page-wide. Only 16 of the 221 nodes declare a category and exactly 4 match
                any given slot — of which one is a declared_only schedule function that
                never has a value — so a positive marker would light up THREE rows out of
                roughly 150 under a caption reading "used for this slot", asserting that
                the other ~147 were not. That is a worse statement than no legend. The
                real device now explains itself on the block that contains it (see
                ZoneSectionView's fadedRowCount).

                USER OVERRIDE — not computed and not computable. `SlotInputNode` carries no
                override field of any kind; the only override vocabulary in the contract is
                the schedule dialog's per-DATE `source`, which is not on a node row. So this
                half was a placeholder copied from the mock, and it goes regardless. */}
          </div>
          <div className={styles.toolbarRight}>
            {/* ── ONE BUTTON FOR THE WHOLE TEARSHEET (UAT 11) ────────────────────────
                Andrew settled this against a control per accordion: the workbook IS the
                whole tearsheet — every block flat on one sheet with the monthly series
                behind it — so a per-section download would promise a narrower file than
                the one it produces. */}
            <TearsheetExportButton
              response={data}
              packageId={packageId}
              calculationId={calculationId}
              slotId={slotId}
            />
            {/* ── THE "OPEN EVALUATION MODEL MAP" BUTTON IS GONE (UAT, 9 Sep) ────────
                It called `setView('model_map')` — the SAME thing as the ContentSwitcher
                immediately to its right, which also shows which view is active. Two
                controls for one action, one of them stateless.

                It was worse than redundant: `renderIcon={Launch}` is the new-window
                icon, so it promised to open the model map somewhere else and then
                switched an in-modal view. Clicked while already on the model map it did
                nothing at all, which is how it read in UAT.

                The ContentSwitcher is the survivor because it does the same job AND
                reports state. Nothing replaces the button. */}
            <ContentSwitcher
              size="sm"
              selectedIndex={view === 'table' ? 0 : 1}
              onChange={({ name }) => setView(name as TearsheetView)}
              className={styles.viewSwitcher}
            >
              <Switch name="table" text="Table" />
              <Switch name="model_map" text="Model map" />
            </ContentSwitcher>
          </div>
        </div>

        <div className={styles.body}>
          {view === 'table' && (
            <>
              {showSkeleton && (
                <div className={styles.tableView}>
                  <SkeletonText heading width="40%" />
                  <SkeletonText paragraph lineCount={5} />
                  <SkeletonText paragraph lineCount={6} />
                </div>
              )}
              {isError && (
                <div className={styles.errorBlock}>
                  <InlineNotification
                    kind="error"
                    lowContrast
                    hideCloseButton
                    title="Tearsheet unavailable"
                    subtitle={tearsheetErrorSubtitle(error)}
                  />
                  {error?.retryable && (
                    <Button
                      kind="tertiary"
                      size="sm"
                      renderIcon={Renew}
                      onClick={() => void refetch()}
                      disabled={isFetching}
                    >
                      Try again
                    </Button>
                  )}
                </div>
              )}
              {data && (
                <TearsheetTableView
                  response={data}
                  slotId={slotId}
                  packageId={packageId}
                  onCommitOverride={onCommitOverride}
                  onRerun={onRerun}
                />
              )}
              {showBlocked && (
                <TearsheetBlockedState
                  calcState={calcState}
                  fetchStatus={fetchStatus}
                />
              )}
            </>
          )}

          {view === 'model_map' && <TearsheetModelMap response={data} />}
        </div>
      </div>
    </>
  );
}
